import { useEffect, useState } from 'react';
import { fetchAPI } from '../../utils/utils';
import WorldMap from '../mapa/WorldMap';
import '../mapa/Mapa.css';

function StatsMapaTab({ castellersInfo }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [days, setDays] = useState(7);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [visibleIntervals, setVisibleIntervals] = useState(20);
    const [isListSelection, setIsListSelection] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [refreshProgress, setRefreshProgress] = useState(0);

    useEffect(() => {
        loadData(true);
    }, [days]);

    // Auto-reload data every 10 seconds
    useEffect(() => {
        const interval = setInterval(() => {
            loadData();
        }, 10000);
        return () => clearInterval(interval);
    }, [days]);

    // Progress bar for auto-refresh countdown
    useEffect(() => {
        const progressInterval = setInterval(() => {
            setRefreshProgress(prev => {
                const newProgress = prev + 1;
                if (newProgress >= 100) return 0;
                return newProgress;
            });
        }, 100);
        return () => clearInterval(progressInterval);
    }, []);

    const loadData = (firstLoad = false) => {
        if (firstLoad) setLoading(true);
        setRefreshProgress(0);
        
        fetchAPI(`/ip-logs?days=${days}`, (response) => {
            setLogs(response || []);
            setLastUpdated(new Date());
            setLoadError(false);
            setLoading(false);
        }, true, () => {
            if (firstLoad) setLoadError(true);
            setLoading(false);
        });
    };

    // Filter out anonymous connections and group by 5-minute bins
    const processLogs = () => {
        const authenticatedLogs = logs.filter(log => 
            log.userId && 
            log.userId !== 'anonymous' && 
            log.userId !== 'null' && 
            log.userId !== -1 && 
            log.userId !== '-1'
        );

        const groupedLogs = {};
        authenticatedLogs.forEach(log => {
            const date = new Date(log.timestamp);
            const minutes = Math.floor(date.getMinutes() / 5) * 5;
            date.setMinutes(minutes, 0, 0);
            const binKey = date.toISOString();
            
            if (!groupedLogs[binKey]) {
                groupedLogs[binKey] = {
                    timestamp: date,
                    userLocations: new Map(),
                    entries: []
                };
            }
            
            const userLocation = `${log.userId}|${log.city || log.country || 'Desconegut'}`;
            if (!groupedLogs[binKey].userLocations.has(userLocation)) {
                groupedLogs[binKey].userLocations.set(userLocation, {
                    userId: log.userId,
                    location: log.city || log.country || 'Desconegut',
                    count: 0
                });
            }
            groupedLogs[binKey].userLocations.get(userLocation).count += log.count || 1;
            groupedLogs[binKey].entries.push(log);
        });

        return Object.values(groupedLogs)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    };

    const processedLogs = processLogs();
    let filteredLogs = processedLogs;
    
    if (selectedCountry) {
        filteredLogs = filteredLogs.filter(logGroup => 
            logGroup.entries.some(log => (log.city || log.country) === selectedCountry)
        );
    }
    
    if (selectedUser) {
        filteredLogs = filteredLogs.filter(logGroup => 
            logGroup.entries.some(log => String(log.userId) === String(selectedUser))
        );
    }

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const endDate = new Date(date.getTime() + 5 * 60 * 1000);
        
        const day = date.toLocaleDateString('ca-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
        });
        
        const startTime = date.toLocaleTimeString('ca-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
        
        const endTime = endDate.toLocaleTimeString('ca-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
        
        return { day, time: `${startTime} - ${endTime}` };
    };

    const getUserDisplayName = (userId) => {
        if (userId === -1 || userId === '-1' || !userId) return null;
        
        const user = castellersInfo?.[userId];
        if (user) {
            const parts = [];
            if (user.nom) parts.push(user.nom);
            if (user.cognom) parts.push(user.cognom);
            if (user.mote) parts.push(`(${user.mote})`);
            return parts.join(' ');
        }
        return userId;
    };

    const getSimplifiedStats = () => {
        const authenticatedLogs = logs.filter(log => 
            log.userId && 
            log.userId !== 'anonymous' && 
            log.userId !== 'null' && 
            log.userId !== -1 && 
            log.userId !== '-1'
        );
        
        const cities = {};
        const users = {};
        authenticatedLogs.forEach(log => {
            const city = log.city || log.country;
            if (city) cities[city] = (cities[city] || 0) + (log.count || 1);
            if (log.userId) users[log.userId] = (users[log.userId] || 0) + (log.count || 1);
        });

        return { cities, users };
    };

    const simplifiedStats = getSimplifiedStats();

    const clearFilters = () => {
        setSelectedCountry('');
        setSelectedUser('');
        setVisibleIntervals(20);
        setIsListSelection(false);
    };

    const handleLocationClick = (location, source = 'list') => {
        if (source === 'map') {
            setSelectedCountry(selectedCountry === location ? '' : location);
            setIsListSelection(false);
        } else {
            if (selectedCountry === location) {
                setSelectedCountry('');
                setIsListSelection(false);
            } else {
                setSelectedCountry(location);
                setIsListSelection(true);
            }
        }
    };

    return (
        <div className="stats-mapa-tab">
            {/* Header with filters */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 20,
                flexWrap: 'wrap',
                gap: 15
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
                    <select 
                        value={days} 
                        onChange={(e) => setDays(parseInt(e.target.value))}
                        className="days-filter"
                    >
                        <option value={1}>Últim dia</option>
                        <option value={7}>Última setmana</option>
                        <option value={30}>Últim mes</option>
                    </select>
                    
                    <select
                        value={selectedCountry}
                        onChange={(e) => setSelectedCountry(e.target.value)}
                        className="country-filter"
                    >
                        <option value="">Totes les ciutats</option>
                        {Object.keys(simplifiedStats.cities || {}).map(city => (
                            <option key={city} value={city}>
                                {city} ({Math.ceil((simplifiedStats.cities[city] || 0) / 10)})
                            </option>
                        ))}
                    </select>
                    
                    {(selectedCountry || selectedUser) ? (
                        <button onClick={clearFilters} className="clear-filters-button">
                            Netejar filtres
                        </button>
                    ) : null}
                </div>
                
                {lastUpdated ? (
                    <div style={{ fontSize: '12px', color: '#64748b', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>Actualitzat: {lastUpdated.toLocaleTimeString('ca-ES')}</span>
                        <div style={{ 
                            width: 60, 
                            height: 3, 
                            backgroundColor: '#e2e8f0', 
                            borderRadius: 2,
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: `${refreshProgress}%`,
                                height: '100%',
                                backgroundColor: '#3b82f6',
                                transition: 'width 0.1s ease-out'
                            }} />
                        </div>
                    </div>
                ) : null}
            </div>

            {/* Active filters */}
            {(selectedCountry || selectedUser) ? (
                <div className="active-filters" style={{ marginBottom: 15 }}>
                    <span>Filtres actius:</span>
                    {selectedCountry ? (
                        <span className="filter-tag">
                            {selectedCountry}
                            <button onClick={() => setSelectedCountry('')}>x</button>
                        </span>
                    ) : null}
                    {selectedUser && getUserDisplayName(selectedUser) ? (
                        <span className="filter-tag">
                            {getUserDisplayName(selectedUser)}
                            <button onClick={() => setSelectedUser('')}>x</button>
                        </span>
                    ) : null}
                </div>
            ) : null}

            {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    Carregant...
                </div>
            ) : loadError ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
                    No s'han pogut carregar les connexions
                </div>
            ) : (
                <>
                    <WorldMap 
                        logs={logs} 
                        selectedCountry={selectedCountry} 
                        onLocationClick={handleLocationClick}
                        isListSelection={isListSelection}
                    />
                    
                    <div className="content-grid" style={{ marginTop: 20 }}>
                        <div className="logs-section">
                            <h3 style={{ marginTop: 0, marginBottom: 15 }}>Qui ha entrat i des d'on?</h3>
                            <div className="logs-container" style={{ maxHeight: 400 }}>
                                {filteredLogs.length === 0 ? (
                                    <p style={{ textAlign: 'center', padding: 30, color: '#64748b' }}>
                                        No hi ha connexions registrades
                                    </p>
                                ) : (
                                    filteredLogs.slice(0, visibleIntervals).map((logGroup, index) => {
                                        const { day, time } = formatDate(logGroup.timestamp);
                                        let userLocationPairs = Array.from(logGroup.userLocations.values());
                                        
                                        if (selectedCountry) {
                                            userLocationPairs = userLocationPairs.filter(pair => pair.location === selectedCountry);
                                        }
                                        if (selectedUser) {
                                            userLocationPairs = userLocationPairs.filter(pair => String(pair.userId) === String(selectedUser));
                                        }
                                        
                                        const showDateSeparator = index === 0 || new Date(filteredLogs[index - 1].timestamp).toDateString() !== new Date(logGroup.timestamp).toDateString();
                                        
                                        return (
                                            <div key={index}>
                                                {showDateSeparator ? (
                                                    <div className="date-separator">{day}</div>
                                                ) : null}
                                                <div className="log-entry">
                                                    <div className="log-header">
                                                        <span className="log-time">{time}</span>
                                                    </div>
                                                    <div className="user-location-pairs">
                                                        {userLocationPairs.map((pair, idx) => {
                                                            const userName = getUserDisplayName(pair.userId);
                                                            if (!userName) return null;
                                                            
                                                            return (
                                                                <div key={idx} className="user-location-pair">
                                                                    <span className="user-name">{userName}</span>
                                                                    <span className="location-badge">{pair.location}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                                
                                {filteredLogs.length > visibleIntervals ? (
                                    <div style={{ textAlign: 'center', marginTop: 15 }}>
                                        <button 
                                            className="load-more-button"
                                            onClick={() => setVisibleIntervals(prev => prev + 20)}
                                        >
                                            Carrega més
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className="charts-section">
                            <div className="chart-card">
                                <h4 style={{ marginTop: 0, marginBottom: 12 }}>Ciutats</h4>
                                <div className="city-stats">
                                    {Object.entries(simplifiedStats.cities || {})
                                        .sort(([,a], [,b]) => b - a)
                                        .slice(0, 8)
                                        .map(([city, count]) => (
                                            <div 
                                                key={city} 
                                                className={`city-stat ${selectedCountry === city ? 'selected' : ''}`}
                                                onClick={() => handleLocationClick(city)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <span>{city}</span>
                                                <span className="count">{Math.ceil(count / 10)}</span>
                                            </div>
                                        ))}
                                </div>
                            </div>

                            <div className="chart-card">
                                <h4 style={{ marginTop: 0, marginBottom: 12 }}>Més actius</h4>
                                <div className="user-stats">
                                    {Object.entries(simplifiedStats.users || {})
                                        .sort(([,a], [,b]) => b - a)
                                        .slice(0, 8)
                                        .map(([userId, count]) => {
                                            const userName = getUserDisplayName(userId);
                                            if (!userName) return null;
                                            
                                            return (
                                                <div 
                                                    key={userId} 
                                                    className={`user-stat ${String(selectedUser) === String(userId) ? 'selected' : ''}`}
                                                    onClick={() => setSelectedUser(String(selectedUser) === String(userId) ? '' : userId)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <span className="user-id">{userName}</span>
                                                    <span className="count">{Math.ceil(count / 10)}</span>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default StatsMapaTab;
